import { Box, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { type CollectionDistrict } from '../../../../../models/peerToPeer/Collection'
import { SetP2PProgramStatus } from '../../../../../services/peerToPeer/PeerToPeerService'
import { WizardFooter } from './WizardFooter'
import { allWritesSucceeded } from './programStatusWrites'
import { stepStyles } from './stepStyles'

interface Props {
  district?: CollectionDistrict
  buildingIds: number[]
  reportingYearId: number
  onBack: () => void
  onContinue: () => void
}

export const BuildingsWithoutStep = ({ district, buildingIds, reportingYearId, onBack, onContinue }: Props): JSX.Element => {
  const [notes, setNotes] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const buildings = (district?.buildings ?? []).filter(b => buildingIds.includes(b.organizationBuildingId))

  const saveAndContinue = async (): Promise<void> => {
    setIsSaving(true)
    // Merge-only: mark these buildings no-program and attach the notes, without wiping
    // any program details a building may still carry from a prior "with a program" pass
    // (retained but hidden).
    const results = await Promise.all(buildingIds.map(async id =>
      await SetP2PProgramStatus({ organizationBuildingId: id, reportingYearId, hasActiveProgram: false, notesToStart: notes })
    ))
    setIsSaving(false)
    if (allWritesSucceeded(results)) onContinue()
  }

  return (
    <Box data-testid='p2pBuildingsWithoutStep'>
      <Typography variant='h3'>Buildings Without a Program</Typography>
      <Typography variant='body2' sx={stepStyles.intro}>Let&apos;s capture some details about the buildings without an active program.</Typography>

      <Typography variant='h4' sx={{ mb: '8px' }}>No Peer to Peer Program</Typography>
      <Typography variant='body1' sx={{ mb: '8px' }}>Verify that the buildings listed below do not have an active Peer to Peer program.</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '480px' }}>
        {buildings.map(building => (
          <Box key={building.organizationBuildingId} sx={{ backgroundColor: 'var(--grey-100)', borderRadius: '4px', px: '12px', py: '10px' }} data-testid={`p2pWithoutBuilding-${building.organizationBuildingId}`}>
            <Typography variant='body1'>{building.name}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ fontStyle: 'italic', display: 'block', mt: '8px' }}>
        Not quite right? Go back to update your selections.
      </Typography>

      <Box sx={{ ...stepStyles.section, mt: '40px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography variant='h4'>Notes</Typography>
          <Typography variant='body2' sx={stepStyles.optionalTag}>Optional</Typography>
        </Box>
        <Box sx={stepStyles.formField}>
          <Typography variant='body1'>Share any additional context about these schools with the START team.</Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={notes}
            onChange={event => { setNotes(event.target.value) }}
            placeholder='Share any context that may be helpful...'
            sx={stepStyles.textArea}
            inputProps={{ 'aria-label': 'Notes' }}
            data-testid='p2pWithoutNotes'
          />
        </Box>
      </Box>

      <WizardFooter
        onBack={onBack}
        onContinue={() => { void saveAndContinue() }}
        continueDisabled={isSaving}
        backTestId='p2pWithoutBack'
        continueTestId='p2pWithoutContinue'
      />
    </Box>
  )
}
